import {Link, useParams} from 'react-router-dom';
import {useGetCampaignById} from '@tinct/core-api';
import {AudienceConflictModal} from '../../../funnels/steps/step4-audience/audience-conflict-modal';
import {usePreviewAndRefine} from '../hooks/use-preview-and-refine';
import {PageCustomCodeSection} from './components/page-custom-code-section';
import {LeadFormSection} from './components/lead-form-section';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@tinct/web-components';
import {Eye, Info, Pencil} from 'lucide-react';
import {ReactNode, useState} from 'react';
import {pluralize, useStaticAssetsUrl} from "@tinct/core-utils";
import {useCurrentWorkspaceUrlPrefix} from "@tinct/core-workspace";
import {isBlockPersonalizationEnabled} from '../../../utils/is-block-personalization-enabled';


type CustomRuleSection = {
  /** Block label the rules apply to — null for rules listed before any heading. */
  label: string | null;
  rules: string[];
};

/**
 * Splits the campaign's custom instructions into their block sections. The backend stores them as
 * "<Block label>:" headings each followed by "- rule" bullets; bullets appearing before any
 * heading form an unlabelled leading section. Anything else (blank lines, prose) is ignored.
 */
function parseCustomInstructions(
  instructions: string | null | undefined
): CustomRuleSection[] {
  const sections: CustomRuleSection[] = [];
  let current: CustomRuleSection | null = null;
  for (const raw of instructions?.split('\n') ?? []) {
    const line = raw.trim();
    if (!line) continue;
    const bullet = /^[-*•]\s+(.*)$/.exec(line);
    if (bullet) {
      if (!current) {
        current = {label: null, rules: []};
        sections.push(current);
      }
      current.rules.push(bullet[1].trim());
    } else if (line.endsWith(':')) {
      current = {label: line.slice(0, -1).trim(), rules: []};
      sections.push(current);
    }
  }
  return sections.filter((section) => section.rules.length > 0);
}

type SettingCardProps = {
  icon: string;
  title: string;
  description: string;
  onEdit?: () => void;
  to?: string;
  onClick?: () => void;
  /** Action button label — defaults to "Edit". */
  actionLabel?: string;
  /** Action button icon — defaults to the pencil. */
  actionIcon?: ReactNode;
};

function SettingCard({
  icon,
  title,
  description,
  onEdit,
  to,
  onClick,
  actionLabel = 'Edit',
  actionIcon = <Pencil className="size-3.5" />,
}: SettingCardProps) {
  const content = (
    <Card className="flex-row items-center gap-4 p-4 shadow-none">
      <img
        src={icon}
        alt=""
        className="size-12 rounded-xl object-cover"
      />
      <div className="flex flex-1 flex-col gap-0.5 min-w-0">
        <span className="text-sm font-medium">{title}</span>
        <span className="text-sm text-muted-foreground truncate">
          {description}
        </span>
      </div>
      {/* Rendered as a span: the whole card is already the clickable target
          (a <button> or a <Link>), and nesting a real button inside either is
          invalid HTML — React flags the button-in-button case as a hydration
          error. */}
      {(onEdit || to || onClick) && (
        <Button asChild variant="outline" size="sm" className="gap-1.5 shrink-0 cursor-pointer">
          <span>
            {actionIcon}
            {actionLabel}
          </span>
        </Button>
      )}
    </Card>
  );

  if (to) {
    return <Link to={to} className="no-underline">{content}</Link>;
  }

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="text-left no-underline">
        {content}
      </button>
    );
  }

  return content;
}

export function SettingsTab() {
  const { id = '' } = useParams<{ id: string }>();

  const assetsUrl = useStaticAssetsUrl();
  const currentWorkspaceUrlPrefix = useCurrentWorkspaceUrlPrefix();
  const funnelBaseUrl = `${currentWorkspaceUrlPrefix}/funnel-campaigns/${id}`;

  const {
    conflicts,
    showConflictModal,
    setShowConflictModal,
    isValidating,
    handlePreviewAndRefine,
    handleExcludeAndContinue,
  } = usePreviewAndRefine(id);
  const { data: campaign } = useGetCampaignById({
    id,
    fields: {
      id: null,
      name: null,
      status: null,
      landingPageOriginType: null,
      landingPage: {
        fields: {
          id: null,
          url: null,
          publishedUrl: null,
          previewUrl: null,
        },
      },
      campaignContext: {
        fields: {
          valueProposition: null,
        },
      },
      customInstructions: null,
      blocks: {
        limit: 200,
        fields: {
          id: null,
          track: null,
          enabled: null,
        },
      },
      statistics: {
        fields: {
          totalPagesCount: null,
        },
      },
    },
  });

  // NEW pages are hosted by Tinct: their live URL is publishedUrl, not the
  // (null) customer URL.
  const landingPageUrl =
    (campaign?.landingPageOriginType === 'new'
      ? campaign?.landingPage?.publishedUrl ?? campaign?.landingPage?.previewUrl
      : campaign?.landingPage?.url) ?? '—';
  const valueProposition =
    campaign?.campaignContext?.valueProposition ?? '—';
  const [showInstructions, setShowInstructions] = useState(false);
  const customRuleSections = parseCustomInstructions(campaign?.customInstructions);
  const customRulesCount = customRuleSections.reduce(
    (count, section) => count + section.rules.length,
    0
  );
  const customRulesDescription = `${customRulesCount} editorial ${pluralize(customRulesCount, 'rule')} learned from your edits, applied to every new page`;
  const enabledBlocks = campaign?.blocks?.filter(isBlockPersonalizationEnabled) ?? [];
  const blocksCount = enabledBlocks.length;
  const eventsCount = enabledBlocks.filter((b) => b.track).length;
  const pagesCount = campaign?.statistics?.totalPagesCount ?? 0;

  return (
    <div className="flex flex-col gap-6">
      {/* Campaign Settings */}
      <div className="flex flex-col gap-6">
        <h2 className="text-xl font-semibold tracking-tight font-heading">
          Campaign settings
        </h2>

        {/* Only meaningful while the campaign is live — draft/ready/unpublished
            campaigns can be edited freely. */}
        {campaign?.status === 'published' && (
          <Alert>
            <Info className="size-4" />
            <AlertTitle>
              Want to change your campaign&apos;s settings?
            </AlertTitle>
            <AlertDescription>
              You have to unpublish it first. Most changes will require the
              regeneration of all page variants.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col gap-4">
          <SettingCard
            icon={`${assetsUrl}/campaign-landing-page-url.png`}
            title="Landing Page URL"
            description={landingPageUrl}
          />
          <SettingCard
            icon={`${assetsUrl}/campaign-value-proposition.png`}
            title="Value proposition"
            description={valueProposition}
            to={`${funnelBaseUrl}/configure`}
          />
          {/* Nothing learned yet — nothing to show. */}
          {campaign?.customInstructions && (
            <SettingCard
              icon={`${assetsUrl}/campaign-value-proposition.png`}
              title="Custom instructions"
              description={customRulesDescription}
              onClick={() => setShowInstructions(true)}
              actionLabel="View"
              actionIcon={<Eye className="size-3.5" />}
            />
          )}
          <SettingCard
            icon={`${assetsUrl}/campaign-blocks.png`}
            title="Personalized Blocks & Events"
            description={`${blocksCount} ${pluralize(blocksCount, 'block')} / ${eventsCount} ${pluralize(eventsCount, 'event')}`}
            to={`${currentWorkspaceUrlPrefix}/campaigns/${id}/studio`}
          />
          <SettingCard
            icon={`${assetsUrl}/campaign-audience.png`}
            title="Audience"
            description={`${pagesCount} ${pluralize(pagesCount, 'company', 'companies')}`}
            to={`${funnelBaseUrl}/audience`}
          />
          <SettingCard
            icon={`${assetsUrl}/campaign-preview.png`}
            title="Preview & Refine"
            description="Browse all page variants and interact with our agents to review everything before publication."
            onClick={handlePreviewAndRefine}
          />
        </div>
      </div>

      {/* Page-scoped custom code — hosted (Tinct-generated) pages only */}
      {campaign?.landingPageOriginType === 'new' && campaign?.landingPage?.id && (
        <PageCustomCodeSection landingPageId={campaign.landingPage.id} />
      )}

      {/* Lead form — hosted (Tinct-generated) pages only */}
      {campaign?.landingPageOriginType === 'new' && campaign?.landingPage?.id && (
        <LeadFormSection landingPageId={campaign.landingPage.id} />
      )}

      <AudienceConflictModal
        open={showConflictModal}
        onOpenChange={setShowConflictModal}
        conflicts={conflicts}
        onExcludeAndContinue={handleExcludeAndContinue}
        isPending={isValidating}
      />

      <Dialog open={showInstructions} onOpenChange={setShowInstructions}>
        <DialogContent className="max-h-[80vh] gap-4 overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Custom instructions</DialogTitle>
            <DialogDescription>
              Editorial rules learned from your page edits, applied to every
              new page of this campaign.
            </DialogDescription>
          </DialogHeader>
          {customRulesCount > 0 ? (
            <div className="flex flex-col gap-4 text-sm text-foreground">
              {customRuleSections.map((section, sectionIndex) => (
                <div key={sectionIndex} className="flex flex-col gap-1.5">
                  {section.label && (
                    <p className="font-medium">{section.label}</p>
                  )}
                  <ul className="flex list-disc flex-col gap-1.5 pl-5">
                    {section.rules.map((rule, index) => (
                      <li key={index}>{rule}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            // Instructions exist but aren't bulleted — show them as-is.
            <p className="whitespace-pre-wrap text-sm text-foreground">
              {campaign?.customInstructions}
            </p>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
